/**
 * Raising a quote, an invoice or a deal from a COMPANY row, with the line items
 * already seeded from that company's projects of interest.
 *
 * Owner's instruction, 2026-09-13: pressing "+" on a company and picking Quote,
 * Invoice or Deal should open the create form with one line per product per
 * project of interest, rather than an empty grid somebody re-types from the
 * projects cell they were just looking at.
 *
 * ⚠️ **MOUNT THIS ONCE, AT THE PAGE — NEVER PER ROW.** It mounts a document form
 * modal, each of which is a large component with its own hooks and its own
 * rate-limited Xero reads. One per row on a list of fifty would be fifty. The
 * companies page already owns the equivalent single mount for its own form
 * modal, and this follows it.
 *
 * ## 🔴 THIS FLOW WRITES NOTHING TO THE COMPANY
 *
 * Unticking a row in the chooser decides **this document only**. There is no
 * `PATCH /companies/{id}` anywhere on this path, no `projectIds` key, and no
 * wording that could be read as taking a project off the company. That write is
 * a **whole-set REPLACE**, and since 2026-09-13 it also closes the removed
 * projects' sales pursuits as **Lost** — with a `200` and nothing on screen. It
 * is unrecoverable in practice, because nothing records WHY a pursuit is Lost,
 * so a re-tick cannot reopen it. **Adding one here would be the quietest
 * destructive write in the app**: a tickbox that looks like a filter.
 *
 * ## The stages, and why the empty one does not stop here
 *
 * ```
 * resolving -> the dialog, with skeletons, while the reads land
 * choosing  -> the dialog, one row per project of interest, everything ticked
 * form      -> the document form, seeded with whatever was chosen
 * ```
 *
 * 🔴 **A COMPANY WITH NO PROJECTS OF INTEREST GOES STRAIGHT TO THE FORM WITH NO
 * LINES** — owner's decision, and exactly what the "+" did before this feature
 * existed. No empty dialog to dismiss: an empty list would read as something
 * having failed to load rather than as a company nobody has recorded projects
 * for, which today is an ordinary state.
 *
 * ⚠️ **AN ERROR IS NOT AN EMPTY LIST AND MUST NOT BEHAVE LIKE ONE.** A failed
 * read cannot tell us whether this company has projects of interest, so the
 * dialog stays open with a retry and a way past. Falling through to the form
 * would silently drop the whole feature exactly when the network is unreliable,
 * and nobody reports a dialog they never knew was meant to appear.
 *
 * ## What this does NOT do
 *
 * ⛔ **PREFILLING IS NOT RAISING.** Nothing is created until the person presses
 * Save inside the form. Every seeded row is visible and adjustable first, and
 * `onSaved` hands the caller nothing but the fact that a save happened — the
 * caller RE-READS. There is no splice here and no optimistic anything.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { quotePreviewHref } from '@/lib/quote-pdf-preview';

import { DealFormModal } from '@/components/deals/DealFormModal';
import { InvoiceFormModal } from '@/components/invoices/InvoiceFormModal';
import { QuoteFormModal } from '@/components/quotes/QuoteFormModal';
import { api, ApiError } from '@/lib/api';
import type { PlannedProductLine } from '@/lib/api';
import {
  orderProductsForSeeding,
  seededLinesForProjects,
  travelProductIds,
} from '@/lib/company-project-lines';
import type { SeedableProjectProduct } from '@/lib/company-project-lines';
import type { Project } from '@/lib/types';

import type {
  CompanyProjectLinesKind,
  CompanyProjectLinesRow,
  CompanyProjectSoldMark,
} from './CompanyProjectLinesDialog';
import { CompanyProjectLinesDialog } from './CompanyProjectLinesDialog';

export type { CompanyProjectLinesKind };

/**
 * What the page asked for. `null` closes the flow.
 *
 * ⚠️ **IT CARRIES ONLY `{id, name}`, AND THE FLOW FETCHES THE REST ITSELF.** A
 * kanban card carries no `projectsOfInterest` and neither does every list shape
 * this will be mounted behind, so taking the array as a prop would work on one
 * mount point and silently seed nothing on another. One `getCompany` per open,
 * uniform across every caller, and never while `request === null`.
 */
export interface CompanyProjectLinesRequest {
  kind: CompanyProjectLinesKind;
  company: { id: number; name: string };
}

export interface CompanyProjectLinesFlowProps {
  request: CompanyProjectLinesRequest | null;
  onClose: () => void;
  /** A document was saved. The caller RE-READS; nothing here patches a list. */
  onSaved: () => void;
}

type Stage =
  | { name: 'resolving' }
  | { name: 'error'; message: string }
  | {
      name: 'choosing';
      rows: CompanyProjectLinesRow[];
      /** Kept so confirm builds its lines through the one shared builder. */
      travelIds: ReadonlySet<number>;
      soldCheckSkippedFor: number | null;
      soldCheckIncomplete: boolean;
    }
  | { name: 'form'; lines: PlannedProductLine[] };

/**
 * Above this many projects of interest, the already-sold check is not made at
 * all and the dialog SAYS SO.
 *
 * 🔴 **THE CHECK COSTS TWO REQUESTS PER PROJECT AND THERE IS NO CHEAPER CORRECT
 * FORM.** A quote or invoice list row carries the document but **not which
 * projects its lines name**, so "has this company already been sold this
 * project" is only answerable by asking the server per project —
 * `?companyId=X&lineProjectId=P&perPage=1`, read back off `meta.total`, once for
 * quotes and once for invoices. At this threshold one press costs 16 requests,
 * on top of the three or four this flow already spends.
 *
 * ⚠️ *SUPERSEDED: "against a limit of **120 per 60 seconds shared across the
 * whole office**."* It is **12,000/60s** (corrected 2026-09-17). The cap is
 * still right and the objection is the SHAPE, not the arithmetic: this is a
 * per-project round trip on a list nothing bounds, so the cost grows with the
 * company rather than with the page, and one press of one control is the only
 * place it can be bounded. The budget that reason cited is off by a factor of
 * a hundred.
 *
 * ⛔ **SKIPPING IT MUST BE VISIBLE.** An unmarked row and an unchecked row look
 * identical, and the wrong reading — *nobody has quoted this* — is the one that
 * gets acted on. See `soldCheckSkippedFor` on the dialog.
 */
const SOLD_CHECK_MAX_PROJECTS = 8;

/**
 * Everything a project sells.
 *
 * ⚠️ **`products` IS THE SET, and it is now the only thing read here.** Since
 * migration 156 a project sells several, and matching the primary alone silently
 * drops every secondary product — which here means dropping a travel package off
 * a customer's document.
 *
 * ✅ **The deprecated-singular fallback is GONE — 2026-09-18.** ⚠️ *SUPERSEDED:
 * "The fallback is for a server older than that and **must never become the
 * first choice**."* It was one of the last readers of the key the backend is
 * withdrawing, and it had already stopped being reachable: `d_projects.product_id`
 * is `NOT NULL` and `ensurePrimaryProduct` re-asserts it into the set on every
 * write, so `products` is never empty against a server that has run 156.
 *
 * ⛔ **Nor could it safely be left to rot.** Once the key is withdrawn
 * `project.product` reads `undefined`, and the guard was written `=== null` — so
 * the branch would have returned `[undefined]` rather than `[]`, and the first
 * `.id` read off that is a crash or a blank product on a customer's document.
 * **A dead branch whose guard tests the wrong absence is worse than a live one.**
 *
 * ⚠️ Deliberately a twin of `productsOf` in
 * `@/components/sales-tracker/CombineDocumentFlow` rather than an import from
 * it: that file is another feature's flow, not a library, and importing a
 * private helper across two features is how one of them ends up unable to change
 * it. If a third caller appears, it moves DOWN into `@/lib`.
 */
/*
 * 🔴 **THE RETURN TYPE IS `SeedableProjectProduct[]`, NOT `MasterRef[]`, AND THAT
 * IS A CORRECTNESS FIX RATHER THAN TIDYING — 2026-09-13.** A project's products
 * now carry `seedsLineItems`, the per-product flag deciding whether the "+" pulls
 * that product into a document's lines. `MasterRef` is `{id, name}`, so annotating
 * this as one **erased the flag STATICALLY while the runtime objects still carried
 * it**: the filter downstream kept working, purely because the annotation happened
 * to be a widening rather than a cast. **Nothing would have failed** — and the next
 * reader meets a `MasterRef[]` and helpfully strips a field nothing appears to use.
 *
 * ⚠️ **The flag stays OPTIONAL on that type, and its reason MOVED rather than
 * went — corrected 2026-09-18.** ⚠️ *SUPERSEDED: "**The fallback is exactly why
 * the flag is OPTIONAL on that type.** The deprecated singular `project.product`
 * is a `MasterRef` and can never carry a flag, so this function can genuinely
 * produce an entry without one."* That was true and is no longer THIS function's
 * business — but `SeedableProjectProduct` is a STRUCTURAL type with other
 * producers, and the `{id, name}` rows a company read carries have never had a
 * flag either. ⛔ **Do not "fix" the optionality on the strength of this one
 * caller now always supplying it**: it is the wire type that is required, and
 * what an absent flag means is still decided once, at `productSeedsLineItems`,
 * towards SEEDING.
 */
function productsOf(project: Project | undefined): SeedableProjectProduct[] {
  if (project === undefined) return [];
  return [...project.products];
}

/**
 * A page's `meta.total`, or `null` when it is not a usable number.
 *
 * Read DEFENSIVELY: `Paginated<T>` declares `meta` present and `total` a number,
 * and a type is a claim about the contract rather than a fact about the bytes —
 * this app deploys separately from the API and can be newer than it. A `null`
 * here becomes an UNKNOWN mark, never a `'none'`.
 */
function totalOf(page: { meta: { total: number } }): number | null {
  const total = page.meta.total;
  return typeof total === 'number' && Number.isFinite(total) ? total : null;
}

/**
 * Whether this company already has a quote or an invoice naming this project.
 *
 * ⚠️ **ANY DOCUMENT COUNTS, WHATEVER ITS STATE** — owner's decision. A draft
 * quote and a paid invoice both mean somebody has already put this project in
 * front of this company, which is the fact the reader is weighing; narrowing by
 * stage or status would mark fewer rows and mark them for a reason nobody asked
 * about.
 *
 * ⚠️ **A FAILED CHECK ANSWERS `null`, NEVER `'none'`.** The dialog then draws no
 * badge and the footnote says the check did not complete, so an unmarked row
 * makes no claim in either direction. Resolving a failure to `'none'` would put
 * a confident wrong answer on screen, which is the one thing this mark must not
 * do.
 */
async function soldMarkFor(
  companyId: number,
  projectId: number,
  signal: AbortSignal,
): Promise<CompanyProjectSoldMark | null> {
  try {
    const [quotes, invoices] = await Promise.all([
      api.listQuotes({ companyId, lineProjectId: projectId, perPage: 1 }, signal),
      api.listInvoices({ companyId, lineProjectId: projectId, perPage: 1 }, signal),
    ]);
    const quoted = totalOf(quotes);
    const invoiced = totalOf(invoices);
    // One unusable half makes the whole row unknown. Reporting "on a quote" off
    // a readable quote count while the invoice count was nonsense would be a
    // half-answer wearing a whole one's badge.
    if (quoted === null || invoiced === null) return null;
    if (quoted > 0 && invoiced > 0) return 'both';
    if (quoted > 0) return 'quote';
    if (invoiced > 0) return 'invoice';
    return 'none';
  } catch {
    return null;
  }
}

/**
 * A fingerprint of the seeded basket, for the mount key below.
 *
 * The project id is in it deliberately: the SAME product under two different
 * projects is two legitimate lines — line identity is
 * `(parent_id, product_id, project_key)` — so a fingerprint over products alone
 * would call two different baskets equal.
 */
function seedKeyOf(lines: readonly PlannedProductLine[]): string {
  return lines.map((line) => `${line.productId}@${line.project?.id ?? ''}`).join(',');
}

export function CompanyProjectLinesFlow({
  request,
  onClose,
  onSaved,
}: CompanyProjectLinesFlowProps) {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>({ name: 'resolving' });
  /** Bumped by Try again, so the effect re-runs without the request changing. */
  const [attempt, setAttempt] = useState(0);

  /**
   * The request, held in a REF so it is not an effect dependency.
   *
   * ⚠️ **THE PAGE BUILDS A FRESH OBJECT LITERAL PER ROW AND PER RENDER**, so
   * depending on it would abort and re-issue every read underneath an open
   * dialog on any unrelated page render — discarding the ticks somebody had
   * already made and restarting the sold check. The effect keys on the two
   * primitives below and reads the object through here.
   */
  const requestRef = useRef(request);

  /*
   * ⚠️ **WRITTEN IN AN EFFECT, NOT DURING RENDER, AND IT MUST STAY DECLARED
   * ABOVE THE READ BELOW.** Effects run in declaration order within a commit, so
   * this one has refreshed the ref before the fetch effect reads it. Writing a
   * ref during render is the thing React asks you not to do — it is unsafe under
   * a double-invoked render — and `useRef`'s own initialiser already holds the
   * right value on the first pass, so nothing is lost by deferring it.
   */
  useEffect(() => {
    requestRef.current = request;
  });

  const companyId = request?.company.id ?? null;
  const kind = request?.kind ?? null;

  useEffect(() => {
    if (companyId === null || kind === null) return;

    const controller = new AbortController();
    const { signal } = controller;
    setStage({ name: 'resolving' });

    void (async () => {
      try {
        /*
         * ⚠️ **THE COMPANY READ IS FIRST AND ALONE, DELIBERATELY.** Firing the
         * project and product reads beside it would be two wasted requests
         * against a shared budget for every company that records no projects of
         * interest — which is the majority case and the one that costs exactly
         * one request as written. The extra round trip is paid only where there
         * is something to seed.
         */
        const companyRead = await api.getCompany(companyId, signal);
        if (signal.aborted) return;

        const interest = companyRead.data.projectsOfInterest;
        if (interest.length === 0) {
          // Owner's decision: no empty dialog. Straight to the form with no
          // lines, which is exactly what the "+" did before this existed.
          setStage({ name: 'form', lines: [] });
          return;
        }

        /*
         * Both of these page to the server's ceiling and THROW rather than
         * resolve short, which is why there is no truncation footnote for
         * either:
         *
         *  - `/projects` is over its 100-row cap today (113 active, measured
         *    2026-09-12), so a single-page read would be short by the SAME ~13
         *    projects every time — and a project of interest missing from it
         *    reads as a project that sells nothing.
         *  - the product catalogue decides which products are TRAVEL PACKAGES,
         *    which is a question about the WHOLE set: a product missing from a
         *    short page is indistinguishable from a product nothing points at.
         *
         * ⚠️ **NEITHER IS NARROWED TO ACTIVE ROWS.** A company's projects of
         * interest may legitimately include a RETIRED project, and a retired
         * travel product is still the thing another product points at. Narrowing
         * here would drop a row, or mis-order a line, with nothing on screen
         * saying anything was withheld.
         */
        const [projects, products] = await Promise.all([
          api.listAllProjects(signal),
          api.listAllProducts(signal),
        ]);
        if (signal.aborted) return;

        const travelIds = travelProductIds(products);
        const byId = new Map<number, Project>();
        for (const project of projects) byId.set(project.id, project);

        /*
         * ⚠️ **THE COMPANY'S ORDER IS PRESERVED EXACTLY, and the company's NAME
         * for the project is what is rendered.** The projects cell somebody
         * pressed "+" beside lists them in this order; re-sorting here — or
         * preferring the project read's name — would make the dialog disagree
         * with the row it was opened from.
         *
         * ⚠️ `products: []` means the project could not be resolved (absent from
         * the read, or carrying nothing), NEVER that it sells nothing:
         * `d_projects.product_id` is `NOT NULL` and the server re-asserts it on
         * every write. The dialog disables that row and says why.
         */
        const rows: CompanyProjectLinesRow[] = interest.map((entry) => ({
          id: entry.id,
          name: entry.name,
          products: orderProductsForSeeding(productsOf(byId.get(entry.id)), travelIds),
          sold: null,
        }));

        /*
         * ⚠️ **FIRED ONCE, HERE, ON OPEN — never per render and never per row
         * render.** See {@link SOLD_CHECK_MAX_PROJECTS} for the arithmetic and
         * for why it is skipped outright above the threshold rather than
         * throttled: a partial answer over a shared rate limit is the same
         * unmarked row with a slower page.
         */
        const skipped = rows.length > SOLD_CHECK_MAX_PROJECTS;
        let incomplete = false;

        if (!skipped) {
          const marks = await Promise.all(
            rows.map((row) => soldMarkFor(companyId, row.id, signal)),
          );
          if (signal.aborted) return;
          marks.forEach((mark, index) => {
            const row = rows[index];
            if (row === undefined) return;
            row.sold = mark;
            if (mark === null) incomplete = true;
          });
        }

        setStage({
          name: 'choosing',
          rows,
          travelIds,
          soldCheckSkippedFor: skipped ? rows.length : null,
          soldCheckIncomplete: incomplete,
        });
      } catch (caught) {
        if (signal.aborted) return;
        setStage({
          name: 'error',
          message:
            caught instanceof ApiError
              ? (caught.formMessage ?? caught.message)
              : "This company's projects could not be read.",
        });
      }
    })();

    return () => controller.abort();
  }, [companyId, kind, attempt]);

  /**
   * ⚠️ **THE STAGE IS RESET HERE, AND THAT IS THE FRESHNESS GUARANTEE.** The flow
   * always re-enters at `resolving`, so a second open can never render the first
   * open's basket even for a frame, and the form below is not mounted at all
   * until a fresh basket exists.
   *
   * ⛔ **NOTHING HERE RELIES ON THE FORM'S OWN REMOUNT KEY.** Those keys live in
   * files this feature does not own and have already been wrong once about
   * exactly this shape — `QuoteFormModal`'s `createKeyOf` short-circuited on
   * `lockedCompany` BEFORE it reached the line fingerprint, which made the
   * fingerprint unreachable for every caller passing a lock, i.e. this one. The
   * reset, the conditional mount and the explicit key below are three
   * independent guards, and the cost of all three is nil.
   */
  const close = useCallback(() => {
    setStage({ name: 'resolving' });
    onClose();
  }, [onClose]);

  if (request === null) return null;

  const company = request.company;

  /*
   * ⚠️ **ONLY THE FORM FOR THE REQUESTED KIND IS RENDERED, AND ONLY ONCE THERE IS
   * A BASKET.** `CombineDocumentFlow` mounts both of its forms and derives
   * `open`, which is right when a flow can end in either — here the kind is known
   * at open, and each of these components carries its own hooks and (on the
   * invoice form, on create) a rate-limited Xero read. Mounting the two that
   * cannot be used would spend that for nothing, which is the same argument as
   * mounting this flow once at the page rather than per row.
   *
   * 🔴 **`lockedCompany` RATHER THAN `prefill.company`, and the two are not
   * interchangeable.** Every seeded line names a project THIS company holds as a
   * project of interest; changing the company would leave a basket of projects
   * the new company has no interest in and no pursuit for — a perfectly valid
   * document that advances nothing and describes work nobody discussed with
   * them. The company is the reason the form was opened, which is what
   * `lockedCompany` is for, and `prefill` is documented as a seed that "must
   * never grow into" a lock.
   *
   * ⚠️ **`prefill` IS SPREAD CONDITIONALLY** so a form opened with no lines
   * carries no `prefill` at all, rather than an empty basket that still
   * participates in a remount key.
   */
  if (stage.name === 'form') {
    const lines = stage.lines;
    const seeding = lines.length > 0;
    const key = `${company.id}:${request.kind}:${seedKeyOf(lines)}`;

    const saved = () => {
      onSaved();
      close();
    };

    if (request.kind === 'quote') {
      return (
        <QuoteFormModal
          key={key}
          open
          onClose={close}
          lockedCompany={company}
          {...(seeding ? { prefill: { lines } } : {})}
          onSaved={saved}
          onCreatedPreview={(created) => {
            onSaved();
            close();
            navigate(quotePreviewHref(created.id));
          }}
        />
      );
    }

    if (request.kind === 'invoice') {
      return (
        <InvoiceFormModal
          key={key}
          open
          onClose={close}
          lockedCompany={company}
          {...(seeding ? { prefill: { lines } } : {})}
          onSaved={saved}
        />
      );
    }

    return (
      <DealFormModal
        key={key}
        open
        onClose={close}
        lockedCompany={company}
        {...(seeding ? { prefill: { lines } } : {})}
        onSaved={saved}
      />
    );
  }

  return (
    <CompanyProjectLinesDialog
      open
      onClose={close}
      kind={request.kind}
      companyName={company.name}
      rows={stage.name === 'choosing' ? stage.rows : []}
      loading={stage.name === 'resolving'}
      error={stage.name === 'error' ? stage.message : null}
      onRetry={() => setAttempt((value) => value + 1)}
      onConfirm={(chosen) =>
        setStage({
          name: 'form',
          /*
           * ⚠️ **ONE LINE BUILDER, AND THIS IS NOT IT.** `seededLinesForProjects`
           * owns the one-line-per-product-per-project rule, the
           * participation-first ordering and the `units: null` that makes every
           * seeded row arrive at quantity 1 exactly as a hand-added one does. A
           * second builder here would be a second home for all three, and the two
           * would agree the day they were written and diverge on the first
           * correction — on a document a customer is billed against.
           *
           * Re-ordering the already-ordered rows is harmless: it is a stable
           * partition, so a second pass changes nothing.
           */
          lines: seededLinesForProjects({
            projects: chosen,
            travelProductIds: stage.name === 'choosing' ? stage.travelIds : new Set<number>(),
          }),
        })
      }
      soldCheckSkippedFor={stage.name === 'choosing' ? stage.soldCheckSkippedFor : null}
      soldCheckIncomplete={stage.name === 'choosing' && stage.soldCheckIncomplete}
    />
  );
}
